import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MousePointerClick, Users, Heart, Bookmark, Share2, List, TrendingUp, Package } from 'lucide-react';
import { API, authFetch } from '@/App';

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-4 flex items-start gap-3" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function MiniBar({ value, max }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-pink-500 to-orange-400 rounded-full transition-all"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function CreatorDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await authFetch(`${API}/analytics/creator-dashboard`);
      if (response.ok) setData(await response.json());
    } catch (e) {
      console.error('Failed to load dashboard:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Failed to load insights.</p>
      </div>
    );
  }

  const maxChecklist = data.top_checklists?.[0]?.clicks || 1;
  const maxProduct = data.top_products?.[0]?.clicks || 1;
  const maxTrend = Math.max(...(data.daily_trend?.map(d => d.clicks) || [1]));

  return (
    <div className="min-h-screen pb-24 bg-background" data-testid="creator-dashboard-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full" data-testid="dashboard-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Creator Insights
          </h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-6">
        {/* Overview Stats */}
        <section data-testid="overview-section">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Overview</h2>
          <div className="grid grid-cols-2 gap-3">
            <StatCard icon={MousePointerClick} label="Product Clicks" value={data.total_clicks} sub={`${data.clicks_30d} in last 30 days`} color="bg-gradient-to-br from-pink-500 to-rose-500" />
            <StatCard icon={Users} label="Unique Clickers" value={data.unique_clickers} color="bg-gradient-to-br from-violet-500 to-purple-500" />
            <StatCard icon={Heart} label="Post Likes" value={data.total_post_likes} color="bg-gradient-to-br from-red-400 to-pink-500" />
            <StatCard icon={Bookmark} label="Look Saves" value={data.total_look_saves} color="bg-gradient-to-br from-orange-400 to-amber-500" />
            <StatCard icon={Share2} label="Checklist Shares" value={data.total_shares} color="bg-gradient-to-br from-blue-400 to-cyan-500" />
            <StatCard icon={List} label="Checklists" value={data.total_checklists} color="bg-gradient-to-br from-emerald-400 to-green-500" />
          </div>
        </section>

        {/* Click Trend */}
        {data.daily_trend?.length > 0 && (
          <section data-testid="trend-section">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              <TrendingUp className="w-4 h-4 inline mr-1" />
              Click Trend (30 days)
            </h2>
            <div className="bg-white rounded-2xl border border-border p-4">
              <div className="flex items-end gap-[3px] h-28">
                {data.daily_trend.map((d, i) => {
                  const pct = maxTrend > 0 ? (d.clicks / maxTrend) * 100 : 0;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                      <div
                        className="w-full bg-gradient-to-t from-pink-500 to-orange-400 rounded-t-sm min-h-[2px] transition-all group-hover:opacity-80"
                        style={{ height: `${Math.max(pct, 2)}%` }}
                      />
                      <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-foreground text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none">
                        {d.clicks} clicks
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-[10px] text-muted-foreground">
                  {data.daily_trend[0]?.date?.slice(5)}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {data.daily_trend[data.daily_trend.length - 1]?.date?.slice(5)}
                </span>
              </div>
            </div>
          </section>
        )}

        {/* Top Checklists */}
        {data.top_checklists?.length > 0 && (
          <section data-testid="top-checklists-section">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              <List className="w-4 h-4 inline mr-1" />
              Top Checklists by Clicks
            </h2>
            <div className="bg-white rounded-2xl border border-border divide-y divide-border">
              {data.top_checklists.map((cl, i) => (
                <div key={cl.checklist_id || i} className="p-3 space-y-1.5" data-testid={`top-checklist-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-bold text-primary/50 w-5 text-center shrink-0">#{i + 1}</span>
                      <span className="text-sm font-medium truncate">{cl.checklist_name}</span>
                    </div>
                    <span className="text-sm font-bold text-foreground shrink-0 ml-2">{cl.clicks}</span>
                  </div>
                  <MiniBar value={cl.clicks} max={maxChecklist} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Top Products */}
        {data.top_products?.length > 0 && (
          <section data-testid="top-products-section">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              <Package className="w-4 h-4 inline mr-1" />
              Top Products by Clicks
            </h2>
            <div className="bg-white rounded-2xl border border-border divide-y divide-border">
              {data.top_products.map((p, i) => (
                <div key={p.product_id || i} className="p-3 space-y-1.5" data-testid={`top-product-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-bold text-primary/50 w-5 text-center shrink-0">#{i + 1}</span>
                      <div className="min-w-0">
                        <span className="text-sm font-medium truncate block">{p.product_name}</span>
                        <span className="text-xs text-muted-foreground">{p.brand}</span>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-foreground shrink-0 ml-2">{p.clicks}</span>
                  </div>
                  <MiniBar value={p.clicks} max={maxProduct} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Empty state */}
        {data.total_clicks === 0 && data.total_post_likes === 0 && data.total_look_saves === 0 && (
          <div className="text-center py-12 space-y-3" data-testid="empty-state">
            <TrendingUp className="w-12 h-12 text-muted-foreground/30 mx-auto" />
            <p className="text-muted-foreground font-medium">No insights yet</p>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">
              Start creating posts and Looks with product checklists. As people engage with your content, you'll see your stats here.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
